import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Checkbox,
  Paper,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { Add, Delete } from "@material-ui/icons";
import { useBmapi } from "../utils/bmapi-context";
import { TimePicker } from "../ui/DatePicker";
import NumberInput from "../ui/NumberInput";
import { useIntl } from "react-intl";
import { getErrorMessageString } from "../utils/errors";

/* ===================== UTILS ===================== */

const serviceId = "prof";

function getInitials(p) {
  const first = p.first_name?.[0] || "";
  const last = p.last_name?.[0] || "";
  return (first + last).toUpperCase();
}

function formatTime(date) {
  return date.toTimeString().slice(0, 5);
}

function addMinutes(timeStr, minutes) {
  const [h, m] = timeStr.split(":").map(Number);
  const d = new Date();
  d.setHours(h);
  d.setMinutes(m + minutes);
  return formatTime(d);
}

function generateSlots(ranges, duration) {
  const slots = [];
  if (!duration || duration <= 0) return slots;

  ranges.forEach(({ start, end }) => {
    if (!start || !end) return;

    let current = new Date(start);

    while (current < end) {
      const next = new Date(current.getTime() + duration * 60000);
      if (next > end) break;

      slots.push(formatTime(current));
      current = next;
    }
  });

  return slots;
}

function getDateText(date) {
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}

/* ===================== HEADER ===================== */

function ProfessionalHeader({ p }) {
  return (
    <Box display="flex" alignItems="center" minWidth={180} px={1}>
      <Avatar
        src={p.image_url || undefined}
        style={{
          marginRight: 8,
          backgroundColor: !p.image_url ? "#1976d2" : undefined,
        }}
      >
        {!p.image_url && getInitials(p)}
      </Avatar>
      <Typography variant="body2">
        {p.first_name} {p.last_name}
      </Typography>
    </Box>
  );
}

/* ===================== COMPONENT ===================== */

export default function ReservationsConfigDynamic({ fair, onDirtyChange }) {
  const intl = useIntl();
  const { bmapi, notifyError, notifySuccess } = useBmapi();

  const [professionals, setProfessionals] = useState([]);
  const [slotDuration, setSlotDuration] = useState("");
  const [ranges, setRanges] = useState([{ start: null, end: null }]);

  const [availability, setAvailability] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);

  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false); // ✅ NEW

  /* ---------- LOAD PROFESSIONALS ---------- */

  useEffect(() => {
    if (bmapi) {
      bmapi.getProfessionals({ category: "mentor" }).then((resp) => {
        setProfessionals(resp || []);
      });
    }
  }, [bmapi]);

  /* ---------- HYDRATE ---------- */

  const hydrateFromSlots = useCallback((slotsData) => {
    const newAvailability = {};
    const times = new Set();

    slotsData.forEach((slot) => {
      const { date, start_time, professional_id } = slot;

      times.add(start_time);

      if (!newAvailability[date]) newAvailability[date] = {};
      if (!newAvailability[date][start_time])
        newAvailability[date][start_time] = [];

      newAvailability[date][start_time].push(professional_id);
    });

    if (slotsData.length) {
      const first = slotsData[0];

      const [h1, m1] = first.start_time.split(":").map(Number);
      const [h2, m2] = first.end_time.split(":").map(Number);

      const d1 = new Date();
      const d2 = new Date();

      d1.setHours(h1, m1);
      d2.setHours(h2, m2);

      const duration = (d2 - d1) / 60000;
      setSlotDuration(duration);

      // range FIX: usa duration appena calcolata
      const sorted = Array.from(times).sort();
      if (sorted.length) {
        const start = new Date();
        const end = new Date();

        const [hs, ms] = sorted[0].split(":");
        const [he, me] = sorted[sorted.length - 1].split(":");

        start.setHours(hs, ms);
        end.setHours(he, me);
        end.setMinutes(end.getMinutes() + duration);

        setRanges([{ start, end }]);
      }
    }

    setAvailability(newAvailability);
  }, []);

  /* ---------- LOAD CONFIG ---------- */

  useEffect(() => {
    if (!bmapi || !fair?.id) return;

    const load = async () => {
      try {
        const resp = await bmapi.getProfessionalReservationsConfig(serviceId, {
          campaign: fair.id,
        });

        if (!resp?.length) return;

        hydrateFromSlots(resp);
      } catch (e) {
        notifyError(getErrorMessageString(e, intl));
      }
    };

    load();
  }, [bmapi, fair, hydrateFromSlots, notifyError, intl]);

  /* ---------- INIT DATE ---------- */

  useEffect(() => {
    if (fair?.dates?.length && !selectedDate) {
      setSelectedDate(fair.dates[0]);
    }
  }, [fair, selectedDate]);

  /* ---------- DIRTY ---------- */

  const markDirty = () => {
    if (!isDirty) {
      setIsDirty(true);
      onDirtyChange?.(true);
    }
  };

  /* ---------- SORT ---------- */

  const professionalsSorted = useMemo(() => {
    return [...professionals].sort((a, b) =>
      (a.last_name || "").localeCompare(b.last_name || "")
    );
  }, [professionals]);

  /* ---------- VALIDATION ---------- */

  const parsedDuration = Number(slotDuration);
  const isDurationValid =
    slotDuration !== "" &&
    Number.isInteger(parsedDuration) &&
    parsedDuration > 0;

  /* ---------- SLOTS ---------- */

  const slots = useMemo(() => {
    if (!isDurationValid) return [];
    return generateSlots(ranges, parsedDuration);
  }, [ranges, parsedDuration, isDurationValid]);

  /* ---------- HANDLERS ---------- */

  const handleTimeChange = (index, field) => (value) => {
    const newRanges = [...ranges];
    newRanges[index][field] = value;
    setRanges(newRanges);
    markDirty();
  };

  const addRange = (index) => () => {
    const newRanges = [...ranges];
    newRanges.splice(index + 1, 0, { start: null, end: null });
    setRanges(newRanges);
    markDirty();
  };

  const deleteRange = (index) => () => {
    const newRanges = [...ranges];
    newRanges.splice(index, 1);
    if (!newRanges.length) newRanges.push({ start: null, end: null });
    setRanges(newRanges);
    markDirty();
  };

  const toggleAvailability = (time, profId) => () => {
    if (!selectedDate) return;

    setAvailability((prev) => {
      const daySlots = prev[selectedDate] || {};
      const slot = daySlots[time] || [];

      markDirty();

      return {
        ...prev,
        [selectedDate]: {
          ...daySlots,
          [time]: slot.includes(profId)
            ? slot.filter((id) => id !== profId)
            : [...slot, profId],
        },
      };
    });
  };

  /* ---------- SAVE ---------- */

  const handleSave = async () => {
    if (!fair?.id || !isDurationValid) return;

    const config = {
      campaign_id: fair.id,
      slots: [],
    };

    Object.keys(availability).forEach((date) => {
      ranges.forEach((range) => {
        const rangeSlots = generateSlots([range], parsedDuration);

        rangeSlots.forEach((time) => {
          const profs = availability[date]?.[time] || [];
          if (!profs.length) return;

          config.slots.push({
            date,
            start_hour: time,
            end_hour: addMinutes(time, parsedDuration),
            professional_ids: profs,
          });
        });
      });
    });

    if (!config.slots.length) {
      notifyError("Nessuno slot configurato");
      return;
    }

    try {
      setSaving(true); // ✅ START LOADING

      await bmapi.saveProfessionalReservationsConfig(serviceId, config);

      notifySuccess("Configurazione salvata correttamente");

      setIsDirty(false);
      onDirtyChange?.(false);
    } catch (e) {
      notifyError(getErrorMessageString(e, intl));
    } finally {
      setSaving(false); // ✅ STOP LOADING
    }
  };

  /* ===================== UI ===================== */

  return (
    <Box mt={2}>
      <Paper elevation={1} style={{ padding: 16 }}>
        {/* GIORNI */}
        <Box mb={3} display="flex" gap={1} flexWrap="wrap">
          {fair?.dates?.map((day) => (
            <Box
              key={day}
              px={2}
              py={1}
              borderRadius={20}
              bgcolor={selectedDate === day ? "#1976d2" : "#eee"}
              color={selectedDate === day ? "#fff" : "#000"}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedDate(day)}
            >
              <Typography variant="body2">{getDateText(day)}</Typography>
            </Box>
          ))}
        </Box>

        {/* DURATA */}
        <NumberInput
          label="Durata slot (minuti)"
          value={slotDuration}
          onChange={(val) => {
            const v = val?.target ? val.target.value : val;
            setSlotDuration(v);
            markDirty();
          }}
          fullWidth
        />

        {/* FASCE */}
        <Box mt={3}>
          {ranges.map((range, i) => (
            <Box key={i} mt={2} display="flex" alignItems="center">
              <TimePicker
                label="Da"
                value={range.start}
                onChange={handleTimeChange(i, "start")}
              />
              <TimePicker
                label="A"
                value={range.end}
                onChange={handleTimeChange(i, "end")}
              />
              <IconButton onClick={addRange(i)}>
                <Add />
              </IconButton>
              <IconButton onClick={deleteRange(i)}>
                <Delete />
              </IconButton>
            </Box>
          ))}
        </Box>

        {/* GRID */}
        <Box mt={4} style={{ overflowX: "auto" }}>
          <Box display="flex">
            <Box minWidth={80} />
            {professionalsSorted.map((p) => (
              <ProfessionalHeader key={p.id} p={p} />
            ))}
          </Box>

          {slots.map((time) => (
            <Box key={time} display="flex" alignItems="center">
              <Box minWidth={80}>{time}</Box>

              {professionalsSorted.map((p) => (
                <Box key={p.id} minWidth={180} textAlign="center">
                  <Checkbox
                    checked={
                      availability[selectedDate]?.[time]?.includes(p.id) ||
                      false
                    }
                    onChange={toggleAvailability(time, p.id)}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {/* SAVE */}
        <Box mt={3} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
